import { Signal, computed, effect, inject, untracked } from '@angular/core'

import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals'
import { isEqual } from 'lodash'

import { Observable, Subscription, finalize, tap } from 'rxjs'

import { HarnessConfigService } from '@flowx/designer/modules/ai-builder/services/harness-config.service'
import { HarnessSessionLifetime } from '@flowx/designer/modules/ai-builder/services/harness-session.lifetime'
import type { HarnessUiContext } from '@flowx/designer/modules/ai-builder/services/harness-ui-context.service'
import { HarnessWriteTargetNavigator } from '@flowx/designer/modules/ai-builder/services/harness-write-target.navigator'
import { HarnessComposerFilesStore } from '@flowx/designer/modules/ai-builder/store/harness-composer-files.store'
import { HarnessConversationDetailsStore } from '@flowx/designer/modules/ai-builder/store/harness-conversation-details.store'
import { HarnessConversationFilesStore } from '@flowx/designer/modules/ai-builder/store/harness-conversation-files.store'
import { HarnessFeedbackStore } from '@flowx/designer/modules/ai-builder/store/harness-feedback.store'
import { HarnessReviewStore } from '@flowx/designer/modules/ai-builder/store/harness-review.store'
import {
  CANCEL_NOT_STOPPED_NOTICE,
  acceptExecutionPreview,
  appendAnswers,
  appendUserMessage,
  applyCancel,
  applyFrame,
  applyRunCut,
  applyRunFailure,
  applyRunFrame,
  applyTurnFailure,
  dropUnsentComment,
  initialHarnessRunState,
  initialHarnessSessionState,
  keepNotRunMessage,
  markTurnNotRun,
  notRunMessage,
  reconcileSessionRecord,
  restoreSession,
  withNotice,
  withoutNotRunMessage,
} from '@flowx/designer/modules/ai-builder/store/harness-session.reducer'
import {
  type FileRefusal,
  FileRefusalReason,
  type FilesInConversation,
} from '@flowx/designer/modules/ai-builder/types/composer-file.types'
import type { HarnessSentFile } from '@flowx/designer/modules/ai-builder/types/conversation-file.types'
import {
  HarnessWriteTarget,
  toRetargetProposal,
  toWriteTarget,
  workspaceIdOf,
} from '@flowx/designer/modules/ai-builder/types/conversation.types'
import {
  HarnessAnswerInput,
  HarnessArtefact,
  HarnessRunState,
  HarnessSessionState,
  HarnessTranscriptEntry,
  HarnessTurnAdmission,
  HarnessTurnComment,
  HarnessTurnOptions,
  RETARGET_KEEP_ANSWER,
  RETARGET_MOVE_ANSWER,
  artefactUnderReview,
  isPlanProposalSettled,
} from '@flowx/designer/modules/ai-builder/types/harness-session.types'
import {
  FeedbackClientContext,
  HARNESS_ERROR_CODES,
  HarnessCancelOutcome,
  HarnessDecision,
  HarnessExecutionPreview,
  HarnessFeedbackVerdict,
  HarnessFrame,
  HarnessMergeAnswer,
  HarnessMessageFeedback,
  HarnessSessionRecord,
  HarnessSessionSnapshot,
  isExecutionInFlight,
  isPlanReviewType,
} from '@flowx/designer/modules/ai-builder/types/harness.types'
import {
  BriefDisplay,
  toBriefDisplay,
} from '@flowx/designer/modules/ai-builder/utils/brief-display.util'
import {
  HarnessCallScope,
  isPlanGate,
  planScopeFor,
  proposedTarget,
  turnScopeFor,
} from '@flowx/designer/modules/ai-builder/utils/call-scope.util'
import {
  withRetriedFile,
  withStoredEntries,
} from '@flowx/designer/modules/ai-builder/utils/conversation-files.util'
import {
  filesInConversation,
  resentFile,
  turnAttachments,
} from '@flowx/designer/modules/ai-builder/utils/file-pick.util'
import {
  harnessErrorCode,
  harnessErrorDetail,
  harnessErrorHttpStatus,
  harnessErrorMessage,
  harnessErrorReason,
} from '@flowx/designer/modules/ai-builder/utils/harness-error.util'
import {
  MergeChoice,
  toMergeConflictDisplay,
  toQuestionDisplay,
} from '@flowx/designer/modules/ai-builder/utils/interrupt-display.util'
import { withMessageIds } from '@flowx/designer/modules/ai-builder/utils/message-ids.util'
import { blockerCount } from '@flowx/designer/modules/ai-builder/utils/open-question.util'
import {
  PlanDisplay,
  toPlanStepIds,
  toSessionPlanDisplay,
} from '@flowx/designer/modules/ai-builder/utils/plan-display.util'
import { toStoryPlanView } from '@flowx/designer/modules/ai-builder/utils/story-plan/story-display.util'
import { THINKING } from '@flowx/designer/modules/ai-builder/utils/text.util'
import { retryPrompt } from '@flowx/designer/modules/ai-builder/utils/transcript-turns.util'
import { HarnessApi, HarnessMessageContext } from '@flowx/designer/shared/api/harness.api'

/** The page context and pending conversation read beside the transcript and canonical run fold.
 * Staging, owner-read details and execution-local choices each have their own store. */
type HarnessScopeState = HarnessRunState & {
  /** The conversation being read to be put on screen — picked from the list, or the tab's own
   * coming back after a reload. Until it lands the rail says so, rather
   * than offering to start a new one. */
  openingSessionId: string | null
}

/** What a turn sent just now reports of itself. Settles once: whichever of the harness's answer,
 * a failure or an abandonment comes first. */
type TurnAdmission = {
  promise: Promise<HarnessTurnAdmission>
  admitted: () => void
  /** The harness did not take the turn: refused, or never reached. */
  unadmitted: (error: unknown) => void
  /** The maker or the conversation left before the harness answered. */
  abandon: () => void
}

const TURN_ABANDONED = 'The conversation was left before the harness took this message.'
const STILL_ANSWERING = 'The assistant is still answering.'
const NOWHERE_TO_SEND_FROM = 'Open a project to talk to the agent.'
const NOT_READY_TO_SEND = 'The message cannot be sent yet.'

const blocked = (reason: string): Promise<HarnessTurnAdmission> =>
  Promise.resolve({ kind: 'blocked', reason })

/** The page's context, unless the caller brings its own: that one replaces it whole. */
const withUiContext = <T extends { uiContext?: HarnessUiContext }>(
  context: T,
  options: HarnessTurnOptions
): T => (options.uiContext ? { ...context, uiContext: options.uiContext } : context)

const initialScopeState: HarnessScopeState = {
  ...initialHarnessRunState,
  openingSessionId: null,
}

/** The builder's conversation with the harness, folded through the pure reducer. Root-provided
 * and owned by the maker, not by screen — the rail, the plan view and the hold read one session,
 * and toggling the sparkles off keeps it. */
export const HarnessSessionStore = signalStore(
  { providedIn: 'root' },
  withState<HarnessSessionState>(initialHarnessSessionState),
  withState<HarnessScopeState>(initialScopeState),
  withComputed(
    ({
      interrupt,
      latestBrief,
      latestPlan,
      storyPlanSnapshot,
      storyPlanProposal,
      runningTools,
      streamState,
      transcript,
      record,
      executionBusy,
    }) => {
      const config = inject(HarnessConfigService)
      // By value: each lifecycle frame brings a new record, and a new plan for the same steps
      // would close the step the maker has open in the spine.
      const plan = computed(
        (): PlanDisplay | null =>
          toSessionPlanDisplay(latestPlan(), storyPlanSnapshot(), record()?.plan ?? null),
        { equal: isEqual }
      )
      const storyPlanView = computed(() => {
        const snapshot = storyPlanSnapshot()
        return snapshot ? toStoryPlanView(snapshot) : null
      })
      const brief = computed((): BriefDisplay | null =>
        latestBrief() ? toBriefDisplay(latestBrief()) : null
      )
      const interruptType = computed(() => interrupt()?.type ?? null)
      const reviewedArtefact = computed(() => artefactUnderReview(interruptType()))
      /** The move a `retarget_review` proposes, null while any other interrupt (or none) is open. */
      const retargetProposal = computed(() =>
        interruptType() === 'retarget_review' ? toRetargetProposal(interrupt()?.payload) : null
      )
      /** Whichever document the open interrupt is asking about. Both carry open questions in the
       * same shape, so everything downstream reads the questions and not the artefact. */
      const reviewedDocument: Readonly<
        Record<HarnessArtefact, Signal<BriefDisplay | PlanDisplay | null>>
      > = { brief, plan }
      const underReview = computed(() => {
        const artefact = reviewedArtefact()

        return artefact ? reviewedDocument[artefact]() : null
      })
      /** Whether each document's review can be approved; `isApprovable` says why. */
      const approvableArtefact: Readonly<Record<HarnessArtefact, () => boolean>> = {
        brief: () => !brief()?.hardFailures.length,
        plan: () => false,
      }
      const planRecord = computed(() => record()?.plan ?? null)
      /** Every step the plan holds, a parked revision folded in: what progress is counted on. */
      const planStepIds = computed(() => toPlanStepIds(planRecord()), { equal: isEqual })
      const execution = computed(() => planRecord()?.execution ?? null)
      /** A story is being implemented. The maker writes between stories, so each run message keeps
       * the progress they were answering. */
      const runInFlight = computed(() => isExecutionInFlight(execution()?.status))
      const isStreaming = computed(() => streamState() === 'streaming')
      /** A confirmed brief makes the record authoritative for display, but leaves its plan
       * review open. Only execution or a terminal plan lets a new turn pass that review. */
      const settledProposal = computed(() => {
        const session = record()
        const plan = session?.plan

        return (
          isPlanReviewType(interruptType()) &&
          !!plan &&
          (!!plan.execution || plan.state === 'COMPLETED' || plan.state === 'ABANDONED') &&
          isPlanProposalSettled(session, storyPlanProposal())
        )
      })

      /** The composer answers an interrupt when one is open, and starts a new turn otherwise. */
      const isAnsweringInterrupt = computed(
        () => !!interrupt() && streamState() === 'awaiting-answer' && !settledProposal()
      )

      /** The workspace a file on the composer goes up in: the one the harness keeps a turn's files
       * in — the write target's once there is one, the turn's before. */
      const uploadWorkspaceId = computed(
        () =>
          record()?.write_target?.workspace_id ??
          turnScopeFor(
            record()?.write_target ?? null,
            config.routeContext(),
            record()?.last_context ?? null
          )?.workspaceId
      )

      /** The workspace the conversation's files live in: the harness stores them under the write
       * target's, and before one under where the maker last spoke. app-manager looks every file
       * up in the workspace the call names, so the page's answers only while nothing is known. */
      const filesWorkspaceId = computed(
        () =>
          record()?.write_target?.workspace_id ??
          workspaceIdOf(record()?.last_context ?? null) ??
          workspaceIdOf(config.routeContext())
      )

      return {
        /** Where the conversation writes, once a change was confirmed (conversation-owned). */
        writeTarget: computed((): HarnessWriteTarget | null => record()?.write_target ?? null),
        plan,
        storyPlanView,
        planUsesStories: computed(() => storyPlanSnapshot() !== null),
        brief,
        interruptType,
        retargetProposal,
        isStreaming,
        isAnsweringInterrupt,
        /** The interrupt is open on the brief, so the document's handles steer rather than read back
         * as the record of what was decided. One source for it: the document and its interactive
         * sections are on this store already, to stage what the maker does with them. */
        isReviewingBrief: computed(
          () => isAnsweringInterrupt() && interruptType() === 'brief_review'
        ),
        /** What the agent is doing now: the tool in flight, "Thinking…" between calls, null when
         * idle. One signal, so every surface waiting on a turn says the same thing. Never a
         * finished tool's label — a caption blinking between a label and nothing reads as flicker. */
        activity: computed((): string | null =>
          streamState() === 'streaming' ? runningTools().at(-1)?.name || THINKING : null
        ),
        /** The turn stopped before its end and a new one is the way on, once no story is being
         * implemented. */
        canRetry: computed(() => streamState() === 'interrupted' && !runInFlight()),
        uploadWorkspaceId,
        filesWorkspaceId,
        /** The last message never ran, so a retryable refusal can send it again once no story is
         * being implemented — though not from another workspace than its files went up in, as none
         * other can read them. */
        canRetryNotRunTurn: computed(() => {
          const held = notRunMessage(transcript())
          const workspaceId = uploadWorkspaceId()

          return (
            !!held &&
            !runInFlight() &&
            (held.files ?? []).every(
              (file) => !file.workspaceId || !workspaceId || file.workspaceId === workspaceId
            )
          )
        }),
        /** The review artefact that arrived last — what the Plan view opens on. */
        latestArtefact: computed(
          (): HarnessArtefact | null =>
            transcript()
              .filter((entry) => entry.type === 'artefact-marker')
              .map((entry) => (entry.type === 'artefact-marker' ? entry.artefact : null))
              .at(-1) ?? null
        ),
        /** Open questions the agent marked as blocking on the artefact under review. The switch
         * picks the document the interrupt is asking about — a brief is not a plan — and the count
         * itself is one rule, read from the questions rather than from either document. */
        blockers: computed(() => blockerCount(underReview()?.openQuestions ?? [])),
        /**
         * Judged on the OPEN interrupt, never on an artefact two turns old — and not on
         * `is_approvable`, which the harness derives from the blockers and so is false whenever a
         * blocking question is open. That is the common case, and exactly when the confirm-once
         * step below has to stay reachable; only `hard_failures` close the door.
         *
         * **A plan is never approved** — the harness refuses it (409); only the description is.
         */
        isApprovable: computed(() => {
          const artefact = reviewedArtefact()

          return artefact ? approvableArtefact[artefact]() : true
        }),

        /** The plan's durable record — null before a plan exists. */
        planRecord,
        planStepIds,
        /** The one run this plan has had, if any. */
        execution,
        runInFlight,
        /** Declining a specialist mutation is not abandoning the plan. Only that gate takes a
         * reject; a plan keeps its history through Abandon instead. */
        canReject: computed(() => interruptType() === 'specialist_review'),
        /**
         * The reactive part of why a new conversation cannot start now: a plan is executing.
         * An open run stream is not reactive, so only `newSession()`'s result covers it.
         */
        newConversationBlocked: computed((): boolean => executionBusy()),
        /** Why a turn cannot start from this page yet: it names no context the harness takes — a
         * project page whose version or branch is still loading. A plan's state never closes the
         * conversation (D14). */
        turnUnavailableReason: computed((): string | null =>
          config.routeContext() ? null : 'Waiting for the project to load…'
        ),
        /** The maker is on a project, so a turn names one: the harness keeps a turn's files under
         * it, and refuses them from an organization or workspace page. */
        onProjectPage: computed(() => config.pageLevel() === 'project'),
      }
    }
  ),
  withMethods(
    (
      store,
      client = inject(HarnessApi),
      config = inject(HarnessConfigService),
      navigator = inject(HarnessWriteTargetNavigator)
    ) => {
      // What the maker has staged on the document in front of them. Owned elsewhere — this store
      // only reads it on the way out and clears it — so it is a collaborator, not state.
      const review = inject(HarnessReviewStore)
      const feedback = inject(HarnessFeedbackStore)
      const conversationDetails = inject(HarnessConversationDetailsStore)
      const lifetime = inject(HarnessSessionLifetime)
      const composerFiles = inject(HarnessComposerFilesStore)
      const conversationFiles = inject(HarnessConversationFilesStore)

      /** The conversation's files from the document center, fresh. Skipped while no workspace is
       * known for them. */
      const loadConversationFiles = async (): Promise<void> => {
        const workspaceId = store.filesWorkspaceId()

        if (workspaceId) {
          await conversationFiles.load(store.sessionId(), workspaceId)
        }
      }

      // Not in state: no view renders a subscription, and a signal would notify readers that
      // never read it.
      let turnStream: Subscription | null = null

      const dropTurnStream = (): void => {
        turnStream?.unsubscribe()
        turnStream = null
      }

      // The turn awaiting its answer, so leaving the conversation settles it: an aborted request
      // reports no error of its own.
      let pendingTurn: TurnAdmission | null = null

      const openAdmission = (
        sentLine: { text: string; at: number; comment?: HarnessTurnComment } | null
      ): TurnAdmission => {
        let settled = false
        let settle: (admission: HarnessTurnAdmission) => void = () => undefined
        const promise = new Promise<HarnessTurnAdmission>((resolve) => {
          settle = resolve
        })
        const settleOnce = (admission: HarnessTurnAdmission): boolean => {
          const first = !settled

          settled = true
          settle(admission)

          return first
        }
        const admission: TurnAdmission = {
          promise,
          admitted: () => void settleOnce({ kind: 'admitted' }),
          unadmitted: (error) => {
            const status = harnessErrorHttpStatus(error)
            const message = harnessErrorMessage(error)
            const outcome: HarnessTurnAdmission =
              status !== undefined && status >= 400 && status < 500
                ? { kind: 'refused', status, message }
                : { kind: 'failed', message }

            // A comment the harness never took is sent again from its box, which has its own
            // text: the line that stood in for it must not stay beside the retry. A 409 is no
            // exception: its reread would keep the line as a not-run message with a Retry that
            // resends the words without the comment's ui_context.
            if (settleOnce(outcome) && sentLine?.comment) {
              patchState(store, (state) => dropUnsentComment(state, sentLine.at, sentLine.text))
            }
          },
          abandon: () => void settleOnce({ kind: 'blocked', reason: TURN_ABANDONED }),
        }

        pendingTurn = admission

        return admission
      }

      const abandonPendingTurn = (): void => {
        pendingTurn?.abandon()
        pendingTurn = null
      }

      /** Where a call is made from (doc 37 N1). A turn is made from the page; a plan call —
       * answering a review, running, amending, abandoning — on the write target, or before one
       * on the project the open proposal writes to. */
      const callScope = (
        scopeFor: typeof planScopeFor,
        proposed = proposedTarget(store.interrupt())
      ): HarnessCallScope | undefined => {
        const record = store.record()

        return (
          scopeFor(
            record?.write_target ?? null,
            config.routeContext(),
            record?.last_context ?? null,
            proposed
          ) ?? undefined
        )
      }

      const turnScope = (): HarnessCallScope | undefined => callScope(turnScopeFor)

      const planCallScope = (): HarnessCallScope | undefined => callScope(planScopeFor)

      /** A conversation read as its owner, keeping the attachments policy it carries. */
      const read = async (sessionId: string): Promise<HarnessSessionSnapshot> => {
        const snapshot = await client.readConversation(sessionId)

        composerFiles.adoptAttachmentsPolicy(snapshot.attachmentsPolicy)

        return snapshot
      }

      const fail = (error: unknown): void =>
        patchState(store, (state) =>
          applyTurnFailure(state, harnessErrorCode(error), harnessErrorMessage(error))
        )

      /**
       * What a session read tells the tab that the stream cannot: the ids the harness filed this
       * conversation's messages under, and whatever feedback is already on them. The SSE frames
       * carry no ids at all, so until this runs a finished turn has nothing to rate.
       */
      const applyConversationRead = (snapshot: HarnessSessionSnapshot): void => {
        feedback.reconcileRead(snapshot.feedback)
        conversationDetails.reconcileRead(snapshot.conversation)
        patchState(store, (state) => ({
          ...(snapshot.record && snapshot.record.revision > (state.record?.revision ?? 0)
            ? reconcileSessionRecord(state, snapshot.record)
            : state),
          transcript: withStoredEntries(
            withMessageIds(state.transcript, snapshot.record?.events ?? []),
            snapshot.record?.events ?? []
          ),
        }))
      }

      /** Re-read what the owner read adds beyond streamed frames: message ids, feedback and
       * conversation standing. A failed reconciliation stays quiet, as it did after a turn. */
      const rereadConversation = async (): Promise<void> => {
        const sessionId = store.sessionId()
        const isLatestRequest = lifetime.latestRequestCheck()

        if (!sessionId) {
          return
        }

        try {
          const snapshot = await read(sessionId)

          if (isLatestRequest()) {
            applyConversationRead(snapshot)
          }
        } catch {
          // Ids the maker cannot see are feedback they cannot leave — not a failure of the turn.
        }
      }

      /** Feedback writes belong to the leaf; the session only supplies the conversation id and
       * the page it is made from, as a turn is. */
      const changeFeedback = (
        messageId: string,
        next: HarnessMessageFeedback | null,
        send: (sessionId: string, call: HarnessCallScope) => Promise<unknown>
      ): void => {
        const sessionId = store.sessionId()
        const call = turnScope()

        if (sessionId && call) {
          feedback.submitChange(messageId, next, () => send(sessionId, call))
        }
      }

      /** `onError` defaults to closing the turn as failed. A turn's own stream passes one that
       * reattaches when the connection is cut, since the turn runs on without it. */
      const consume = (
        frames: ReturnType<typeof client.streamTurn>,
        onError: (error: unknown) => void = fail
      ): void => {
        dropTurnStream()
        const isSameConversation = lifetime.sameConversationCheck()

        turnStream = frames.subscribe({
          next: (frame: HarnessFrame) => {
            if (!isSameConversation()) {
              return
            }
            patchState(store, (state) => applyFrame(state, frame))

            // The turn's messages become rateable once the owner read has their ids.
            if (frame.type === 'end') {
              void rereadConversation()
            }
          },
          error: (error: unknown) => {
            if (isSameConversation()) {
              onError(error)
            }
          },
          // A stream that completes while the state still says `streaming` never delivered its
          // `end` frame. The client already errors on that, so this should be unreachable — but a
          // spinner that never stops is the one outcome worth closing twice.
          complete: () =>
            patchState(store, (state) =>
              isSameConversation() && state.streamState === 'streaming'
                ? applyTurnFailure(
                    state,
                    HARNESS_ERROR_CODES.streamTruncated,
                    'The connection ended before the turn finished.'
                  )
                : state
            ),
        })
      }

      /** The session id, opening one if this is the first turn. Persisted at once: a reload before
       * the first frame should still find the interrupt it is waiting on. */
      const ensureSession = async (
        context: ReturnType<typeof client.captureMessageContext>
      ): Promise<string> => {
        const isLatestRequest = lifetime.latestRequestCheck()
        const existing = store.sessionId()

        if (existing) {
          return existing
        }

        const { session_id } = await client.openSession(context)

        if (isLatestRequest()) {
          patchState(store, { sessionId: session_id })
          rememberConversation(session_id)
        }

        return session_id
      }

      /**
       * Answer the open interrupt. `respond` covers a clarifying answer, feedback on a proposal and
       * structured steering — one wire call, and the agent decides which it was. The interrupt's id
       * travels with every answer; the harness refuses one that names no interrupt, or an interrupt answered
       * elsewhere, which is what stops a stale tab from deciding for the current one.
       *
       * `echo` is what the transcript records for the maker: their words as a "You" turn, or — for
       * what they settled without writing — check-lines under the turn that asked.
       */
      const resume = (
        decision: HarnessDecision,
        message?: string,
        echo: string | readonly HarnessAnswerInput[] | undefined = message,
        allowAdjustments?: boolean,
        options: HarnessTurnOptions = {},
        mergeAnswer?: HarnessMergeAnswer
      ): Promise<HarnessTurnAdmission> => {
        const sessionId = store.sessionId()
        const interruptId = store.interrupt()?.interrupt_id
        // Read before the interrupt is cleared: answering a review acts on the plan, answering a
        // question or a read grant is a turn (doc 37 N1). The proposal the review names is read
        // now too, since the call and its retry are built after the interrupt is gone.
        const proposed = proposedTarget(store.interrupt())
        const proposal = decision === 'approve' ? store.retargetProposal() : null
        const gateScope = isPlanGate(store.interrupt()?.type)
          ? (): HarnessCallScope | undefined => callScope(planScopeFor, proposed)
          : turnScope
        // The headers decide a move: the harness reads the destination's edit access and head
        // from them. The body and context stay the maker's page, which the harness only reads.
        const answerScope = (): HarnessCallScope | undefined => {
          const scope = gateScope()
          return scope && proposal
            ? { ...scope, workspaceId: proposal.to.workspace_id, appId: proposal.to.app_id }
            : scope
        }
        if (!sessionId) {
          return blocked('There is no open conversation to answer in.')
        }

        if (store.isStreaming()) {
          return blocked(STILL_ANSWERING)
        }
        // A turn can accompany a run (D14), but still needs somewhere to be made from.
        const scope = answerScope()

        if (!scope) {
          return blocked(NOWHERE_TO_SEND_FROM)
        }
        // A staged edit asks for a revision, never an approval or rejection.
        if (review.hasSteering() && decision !== 'respond') {
          return blocked('A change is staged: send it as a revision first.')
        }

        // Read out before it is cleared, and cleared here rather than inside the review store:
        // whatever the maker staged rides this turn, so the wire payload and the settled lines
        // have to be taken while it is still there.
        const steering = review.hasSteering() ? review.payload() : undefined
        const settled = review.hasSteering() ? review.describe(store.brief()?.decisions ?? []) : []

        const at = Date.now()
        const turn = openAdmission(
          typeof echo === 'string' ? { text: echo, at, comment: options.comment } : null
        )

        patchState(store, (state) => {
          const staged = settled.length ? appendAnswers(state, settled) : state

          return {
            preview: null,
            ...(typeof echo === 'string'
              ? appendUserMessage(staged, echo, at, [], options.comment)
              : echo?.length
                ? appendAnswers(staged, echo)
                : staged),
            streamState: 'streaming' as const,
            interrupt: null,
            // Only a note on a brief asks for a revision of it, so no other answer makes the next
            // brief "on your note".
            briefRevisionPending:
              decision === 'respond' && state.interrupt?.type === 'brief_review',
          }
        })
        // The adjustments consent is the confirmation's, so only an approval spends it.
        if (decision === 'approve') {
          review.clear()
        } else {
          review.clearSteering()
        }

        // Built at the press, and again for a retry: the retry must name the target the refusal
        // just reported, not the one the first attempt had.
        const start = (): Observable<HarnessFrame> =>
          client.streamResume(
            sessionId,
            decision,
            message,
            steering,
            interruptId,
            withUiContext(client.captureMessageContext(answerScope() ?? scope), options),
            allowAdjustments,
            turn.admitted,
            mergeAnswer
          )

        consume(start(), onRefusal(sessionId, start, turn))

        return turn.promise
      }

      /**
       * A conversation-owned harness refuses a call it cannot take (409) before running anything.
       * Two refusals have a way on that is not the maker's to find:
       *
       * `target_headers_required` — a plan call named the wrong project: the conversation got a
       * target since this tab read it (another tab confirmed a change). The refusal carries the
       * target; the call is made again under it, once. Only a plan call is refused so (doc 37 N1):
       * a turn carries the page's headers.
       *
       * Anything else — a moved context, a stale decision — is shown as the harness said it, over the
       * session as it now stands: the optimistic answer came off the screen with the interrupt it
       * answered, and only a read puts that interrupt back.
       *
       * Every other failure is a cut or a broken stream, handled as it always was.
       */
      const onRefusal =
        (sessionId: string, retry?: () => Observable<HarnessFrame>, turn?: TurnAdmission) =>
        (error: unknown): void => {
          if (harnessErrorHttpStatus(error) !== 409) {
            turn?.unadmitted(error)
            reattachOnCut(sessionId)(error)

            return
          }

          const target =
            harnessErrorReason(error) === 'target_headers_required'
              ? toWriteTarget(harnessErrorDetail(error)?.['write_target'])
              : null
          const record = store.record()

          if (target && record && retry) {
            patchState(store, { record: { ...record, write_target: target } })
            consume(retry(), onRefusal(sessionId, undefined, turn))

            return
          }

          turn?.unadmitted(error)
          rereadAfterRefusal(sessionId, error)
        }

      const rereadAfterRefusal = (sessionId: string, error: unknown): void => {
        const isLatestRequest = lifetime.latestRequestCheck()

        dropTurnStream()
        void read(sessionId)
          .then((snapshot) => {
            if (isLatestRequest() && store.sessionId() === sessionId && snapshot.record) {
              show(store.appVersionId(), sessionId, snapshot)
              patchState(store, (state) => withNotice(state, 'error', harnessErrorMessage(error)))
            } else if (isLatestRequest()) {
              fail(error)
            }
          })
          .catch(() => {
            if (isLatestRequest()) {
              fail(error)
            }
          })
      }

      /** A cut is often the network itself going away, and a read sent at that instant fails with
       * it. So the read waits until the browser is online, and is retried a few times for a blip the
       * browser does not report.
       *
       * ponytail: fixed attempts and delay; move to backoff if longer outages must be ridden out. */
      const readBack = async (sessionId: string, attempt = 1): Promise<HarnessSessionSnapshot> => {
        if (globalThis.navigator?.onLine === false) {
          await nextOnline()
        }

        try {
          return await read(sessionId)
        } catch (error) {
          if (attempt >= READ_BACK_ATTEMPTS) {
            throw error
          }
          await new Promise((resolve) => setTimeout(resolve, READ_BACK_DELAY_MS))

          return readBack(sessionId, attempt + 1)
        }
      }

      /**
       * A cut turn stream is not a dead turn: the harness runs it detached from the request. So a
       * cut is answered by reading where the session stands and showing that — reattaching when the
       * turn is still running. Any other failure, or a session with no record to rebuild from, is
       * reported as it always was.
       */
      const reattachOnCut =
        (sessionId: string) =>
        (error: unknown): void => {
          const appVersionId = store.appVersionId()

          if (harnessErrorCode(error) !== HARNESS_ERROR_CODES.streamTruncated) {
            fail(error)

            return
          }

          const isLatestRequest = lifetime.latestRequestCheck()

          dropTurnStream()
          void readBack(sessionId)
            .then((snapshot) => {
              if (isLatestRequest() && store.sessionId() === sessionId) {
                if (snapshot.record) {
                  show(appVersionId, sessionId, snapshot)
                } else {
                  fail(error)
                }
              }
            })
            .catch(() => {
              if (isLatestRequest()) {
                fail(error)
              }
            })
        }

      /**
       * Follow a turn still running harness-side. The tail replays the turn from its start (or, from
       * another pod, rebuilds it from the stored transcript) before going live, so the session is put
       * back to where that turn began — up to its `user` event — and the turn folds in on top of it,
       * rather than twice.
       *
       * A relay without the route answers 404; the session is then read again until the turn has
       * ended, which the harness's lease bounds.
       */
      const tail = (
        appVersionId: string | null,
        sessionId: string,
        record: HarnessSessionRecord
      ): void => {
        const isLatestRequest = lifetime.latestRequestCheck()
        const scope = turnScope()

        if (!scope) {
          return
        }

        const turnStart = record.events.map((event) => event.event).lastIndexOf('user') + 1

        const restored = restoreSession({ ...record, events: record.events.slice(0, turnStart) })

        patchState(store, restored, {
          transcript: keepNotRunMessage(restored.transcript, store.transcript()),
        })
        consume(client.streamTurnTail(sessionId, 0, scope), (error: unknown) => {
          if (harnessErrorHttpStatus(error) === 404) {
            void awaitTurnEnd(appVersionId, sessionId, isLatestRequest)
          } else if (isLatestRequest()) {
            fail(error)
          }
        })
      }

      // ponytail: a fixed poll while the relay lacks turn/stream; drop it once the route is everywhere.
      const awaitTurnEnd = async (
        appVersionId: string | null,
        sessionId: string,
        isLatestRequest: () => boolean
      ): Promise<void> => {
        try {
          for (;;) {
            await new Promise((resolve) => setTimeout(resolve, TURN_POLL_MS))

            if (!isLatestRequest()) {
              return
            }

            const snapshot = await read(sessionId)

            if (!isLatestRequest()) {
              return
            }

            if (snapshot.record?.status !== 'running') {
              show(appVersionId, sessionId, snapshot)

              return
            }
          }
        } catch (error) {
          if (isLatestRequest()) {
            fail(error)
          }
        }
      }

      /** Put a conversation on screen from its owner's read, replaying its whole record. */
      const show = (
        appVersionId: string | null,
        sessionId: string,
        snapshot: HarnessSessionSnapshot
      ): void => {
        const record = snapshot.record

        if (!record) {
          forgetConversation()

          return
        }

        const restored = restoreSession(record)

        rememberConversation(sessionId)
        feedback.replace(snapshot.feedback)
        conversationDetails.replace(snapshot.conversation ?? null)
        patchState(store, restored, {
          transcript: keepNotRunMessage(
            withMessageIds(restored.transcript, record.events),
            store.transcript()
          ),
          preview: null,
          executionBusy: false,
          executionError: null,
        })
        review.clear()
        // The panel's trigger shows only once the conversation is known to have files.
        void loadConversationFiles()

        // Only `running` is a live turn — never a transcript that merely lacks its `end`.
        if (record.status === 'running') {
          tail(appVersionId, sessionId, record)
        }
      }

      /** Leave the current session where it is and clear the screen for another. */
      /** Something is actually on the wire, and `clear()` would drop the subscription without
       * stopping what it is subscribed to: the harness keeps going with nothing on screen pointing
       * back at it. New and switching conversations are disabled on the same condition — this is
       * the backstop under that, so a caller reaching the store another way cannot walk away from a
       * live run either.
       *
       * The open subscription, not `streamState`: that is set optimistically when the message is
       * appended, before the session even exists, and abandoning a turn whose `openSession` is
       * still pending is the deliberate escape hatch that keeps a late session from attaching. A
       * finished stream leaves its subscription behind closed, so `closed` is the live part. */
      const live = (): boolean =>
        (!!turnStream && !turnStream.closed) || lifetime.isRunStreamOpen() || store.executionBusy()

      const clear = (appVersionId: string | null): void => {
        abandonPendingTurn()
        dropTurnStream()
        lifetime.leaveConversation()
        feedback.reset()
        conversationDetails.reset()
        composerFiles.clear()
        conversationFiles.reset()
        patchState(store, initialHarnessSessionState, {
          ...initialScopeState,
          appVersionId,
        })
        review.clear()
      }

      /** Put files on the composer, checked against what this conversation already holds and
       * uploaded where the turn acts. */
      const attachFiles = (incoming: readonly File[]): Promise<void> =>
        composerFiles.attach(
          incoming,
          (): FilesInConversation =>
            filesInConversation(
              store.transcript(),
              conversationFiles.conversationFiles(),
              conversationFiles.removedFileIds()
            ),
          store.uploadWorkspaceId
        )

      /** A turn that never ran — refused as a whole, or lost before its first frame. Its message
       * stays in the thread with its files, and none of them counts as sent: the maker attaches
       * them again on an empty composer, or retries a refusal the harness calls retryable. */
      const markLastTurnNotRun = (): void => patchState(store, (state) => markTurnNotRun(state))

      /** The policy the conversation list carries, read once on arrival so the composer knows it
       * before any conversation is open. A failed read leaves it to the next list or session read. */
      const readAttachmentsPolicy = async (): Promise<void> => {
        try {
          composerFiles.adoptAttachmentsPolicy(await client.readAttachmentsPolicy())
        } catch {
          // The composer offers no attach until a read carries the policy.
        }
      }

      /** Show where a refused duplicate already is: its chip on the composer, or its row in the
       * file panel, opened for it. */
      const showRefusedFile = ({ reason, duplicateOfId }: FileRefusal): void => {
        if (!duplicateOfId) {
          return
        }

        if (reason === FileRefusalReason.AlreadyInConversation) {
          conversationFiles.open(store.sessionId(), store.filesWorkspaceId())
        }

        composerFiles.highlight(duplicateOfId)
      }

      /** Index a file that failed processing again, by its stored entry. It reads as processing
       * wherever it shows; the panel lists it afresh. Its outcome is this conversation's alone. */
      const retrySentFile = async (id: string): Promise<void> => {
        const isSameConversation = lifetime.sameConversationCheck()

        try {
          const retriedId = await conversationFiles.retry(id, store.filesWorkspaceId())

          if (retriedId && isSameConversation()) {
            patchState(store, (state) => ({
              transcript: withRetriedFile(state.transcript, id, retriedId),
            }))
            await loadConversationFiles()
          }
        } catch (error) {
          if (isSameConversation()) {
            patchState(store, (state) => withNotice(state, 'error', harnessErrorMessage(error)))
          }
        }
      }

      /** Send a refused message again, with the files it carried, when the harness called the
       * refusal retryable. It takes the not-run message's place, refusal and all. The composer
       * stays as the maker left it. */
      const retryNotRunTurn = async (): Promise<void> => {
        const message = notRunMessage(store.transcript())

        if (message && store.canRetryNotRunTurn()) {
          await startTurn(message.text, (message.files ?? []).map(resentFile), message)
        }
      }

      /** Whether a turn cannot start now, whatever it carries. None starts beside a story being
       * implemented. */
      const isTurnBlocked = (): boolean =>
        store.isStreaming() ||
        store.runInFlight() ||
        !!store.turnUnavailableReason() ||
        !turnScope()

      /** The composer's message: its words and every file on it, once they are all attached. A
       * comment brings its own context and chip, and no files. Off a project it carries none, and
       * the files stay on the composer for the next project page. */
      const sendTurn = async (
        prompt: string,
        options: HarnessTurnOptions = {}
      ): Promise<HarnessTurnAdmission> => {
        if (options.comment) {
          return startTurn(prompt, [], undefined, options)
        }

        if (isTurnBlocked()) {
          return blocked(NOT_READY_TO_SEND)
        }

        if (!store.onProjectPage()) {
          return startTurn(prompt, [])
        }

        if (!composerFiles.readyToSend()) {
          return blocked(NOT_READY_TO_SEND)
        }

        return startTurn(prompt, composerFiles.take())
      }

      /** A turn beside a run reads and explains, or queues a change for the next item boundary
       * (D14). The run has a stream of its own. `replacing` is the not-run message a Retry sends
       * again: it gives way, refusal and all, only once the message is really on its way. */
      const startTurn = async (
        prompt: string,
        sent: readonly HarnessSentFile[],
        replacing?: HarnessTranscriptEntry,
        options: HarnessTurnOptions = {}
      ): Promise<HarnessTurnAdmission> => {
        const scope = turnScope()
        const unavailableReason = store.turnUnavailableReason()

        if (store.isStreaming()) {
          return blocked(STILL_ANSWERING)
        }

        if (unavailableReason) {
          return blocked(unavailableReason)
        }

        if (!scope) {
          return blocked(NOWHERE_TO_SEND_FROM)
        }
        const context = withUiContext(client.captureMessageContext(scope), options)
        lifetime.invalidateRequestChecks()
        const isLatestRequest = lifetime.latestRequestCheck()

        composerFiles.refuseRepeats(sent)

        const at = Date.now()
        const admission = openAdmission({ text: prompt, at, comment: options.comment })
        patchState(store, (state) => ({
          ...appendUserMessage(
            replacing
              ? { ...state, transcript: withoutNotRunMessage(state.transcript, replacing) }
              : state,
            prompt,
            at,
            sent,
            options.comment
          ),
          preview: null,
          briefRevisionPending: false,
        }))

        const attachments = turnAttachments(sent)

        // The message is marked not run when the turn never started with its files: a refusal
        // (always a stream frame, never an HTTP error) or a failure before the first frame. The
        // `attachment` frames that open a turn are not its start — a refusal may follow them.
        const turn = (
          sessionId: string,
          turnContext: HarnessMessageContext
        ): Observable<HarnessFrame> => {
          let started = false
          const stream = client
            .streamTurn(sessionId, prompt, turnContext, attachments, admission.admitted)
            .pipe(
              tap({
                next: (frame) => {
                  if (frame.type === 'error' && frame.code.startsWith('attachment_') && !started) {
                    markLastTurnNotRun()
                  }
                  started ||= frame.type !== 'attachment'
                },
                error: () => !started && markLastTurnNotRun(),
              })
            )

          // The turn's files are in the document center now, ready or failed.
          return attachments.length
            ? stream.pipe(finalize(() => void loadConversationFiles()))
            : stream
        }

        try {
          const sessionId = await ensureSession(context)
          if (isLatestRequest()) {
            // A turn carries the page's headers, so no refusal of it names a project to retry under.
            consume(turn(sessionId, context), onRefusal(sessionId, undefined, admission))
          } else {
            admission.abandon()
          }
        } catch (error) {
          if (isLatestRequest()) {
            markLastTurnNotRun()
            admission.unadmitted(error)
            fail(error)
          } else {
            admission.abandon()
          }
        }

        return admission.promise
      }

      /** The read putting `sessionId` on screen is over, however it ended. Matched by id rather
       * than by request generation: a clear has already reset it, and a later open owns it now. */
      const finishOpening = (sessionId: string): void => {
        if (store.openingSessionId() === sessionId) {
          patchState(store, { openingSessionId: null })
        }
      }

      /** Put the tab's own conversation back after a reload, read as its owner: it is the maker's,
       * so it comes back wherever they are. Nothing is picked for them when the tab remembers
       * none — an empty conversation is where a new one starts. */
      const restoreConversation = async (): Promise<void> => {
        const sessionId = recallConversation()

        if (!sessionId) {
          return
        }

        const isLatestRequest = lifetime.latestRequestCheck()

        patchState(store, { openingSessionId: sessionId })

        try {
          const snapshot = await read(sessionId)

          // Not over a conversation the maker started or opened in the meantime.
          if (!isLatestRequest() || store.sessionId() || store.streamState() !== 'idle') {
            return
          }

          show(store.appVersionId(), sessionId, snapshot)
        } catch {
          if (isLatestRequest()) {
            forgetConversation()
          }
        } finally {
          finishOpening(sessionId)
        }
      }

      // Whether this tab has put its conversation back yet: once, on arrival.
      let restored = false

      /** The maker moved, and the conversation goes with them. Nothing is cleared — the version
       * on screen is only where the next turn's context and unsaved-changes checks are read —
       * and the tab's own conversation is put back once, on arrival. */
      const arrive = (appVersionId: string | null): void => {
        if (appVersionId !== null && appVersionId !== store.appVersionId()) {
          patchState(store, { appVersionId })
        }

        if (!restored) {
          restored = true
          void readAttachmentsPolicy()
          void restoreConversation()
        }
      }

      return {
        arrive,

        /** Bounded writes used by execution. Frames still fold transcript and run state together. */
        foldRunFrame: (frame: HarnessFrame, expectedSessionId?: string): void => {
          if (expectedSessionId !== undefined && expectedSessionId !== store.sessionId()) {
            return
          }
          patchState(store, (state) => applyRunFrame(state, frame))
        },
        applyRunCut: (): void => patchState(store, applyRunCut),
        applyRunFailure: (message: string): void =>
          patchState(store, (state) => applyRunFailure(state, message)),
        patchRunState: (
          patch: Partial<Pick<HarnessRunState, 'preview' | 'executionBusy' | 'executionError'>>
        ): void => patchState(store, patch),
        acceptExecutionPreview: (
          preview: HarnessExecutionPreview,
          expectedPlanVersion: number,
          reviewingRestart: boolean
        ): void => {
          if (store.planRecord()?.version !== expectedPlanVersion) {
            patchState(store, {
              executionBusy: false,
              preview: null,
              executionError: 'The plan changed while preparing the review. Review it again.',
            })
            return
          }
          patchState(store, (state) => acceptExecutionPreview(state, preview, reviewingRestart))
        },
        replaceRecord: (record: HarnessSessionRecord): void =>
          patchState(store, (state) => reconcileSessionRecord(state, record, true)),
        restoreSession: (record: HarnessSessionRecord): void =>
          patchState(store, (state) => {
            const restored = restoreSession(record)

            return {
              ...restored,
              transcript: keepNotRunMessage(restored.transcript, state.transcript),
            }
          }),
        applyTurnFailure: fail,
        appendExecutionErrorNotice: (message: string): void =>
          patchState(store, (state) => withNotice(state, 'error', message)),
        rereadConversation,
        readBackAfterStreamCut: readBack,
        planCallScope,

        sendTurn,
        attachFiles,
        showRefusedFile,
        retrySentFile,
        retryNotRunTurn,

        /** An interrupted turn, asked again as a new turn — never as the resume it may have been:
         * that gate is gone. The maker's last words are what is asked. */
        retry: (): void => {
          // The words alone: the interrupted turn's files are already in the conversation, and
          // what sits on the composer is the maker's next message, not this one.
          if (store.canRetry()) {
            void startTurn(retryPrompt(store.transcript()), [])
          }
        },

        /** Prose to the agent: an answer to its question, or a change asked of its proposal. */
        respond: (message: string, options?: HarnessTurnOptions): Promise<HarnessTurnAdmission> =>
          resume(
            'respond',
            message,
            message,
            undefined,
            options,
            store.interruptType() === 'merge_conflict' ? { text: message } : undefined
          ),

        /** A side picked on a merge conflict, recorded as a check-line under the question. */
        chooseMerge: (choice: MergeChoice): void => {
          const { question, options } = toMergeConflictDisplay(store.interrupt()?.payload)
          const label = options.find((option) => option.id === choice)?.label ?? choice

          void resume('respond', label, [{ text: label, question }], undefined, {}, { choice })
        },

        /** Stop the merge on its open conflict; the harness discards it and main stays as it was. */
        cancelMerge: (): void =>
          void resume(
            'respond',
            'Cancel the merge.',
            'Cancelled the merge.',
            undefined,
            {},
            {
              cancel: true,
            }
          ),

        /** An option picked on the agent's question. Same wire call as `respond`, but recorded as
         * a numbered check-line: the maker chose rather than wrote. */
        pick: (option: string): void => {
          const { question } = toQuestionDisplay(store.interrupt()?.payload)

          void resume('respond', option, [{ text: option, question }])
        },

        /**
         * Confirm the description — in two presses when blockers are outstanding. Disabling
         * approval instead would make a proposal unapprovable in the common case, since nearly
         * every open question comes back blocking. This is the lifecycle's one approval: it pins a
         * committed baseline and has the harness prepare the work items, and authorises no write
         * and no branch — hence *confirmed*, not *approved*.
         */
        approve: (): void => {
          if (review.hasSteering() || !store.isApprovable()) {
            return
          }

          if (store.blockers() && !review.confirmingApproval()) {
            review.confirmApproval()

            return
          }

          // Recorded as the maker's turn: a transcript in which the agent simply proceeds reads
          // as the agent having decided. The words are the button's own. A conversation-owned
          // confirmation also says whether the agent may adjust the plan it prepares (D15).
          const confirmingBrief = store.interruptType() === 'brief_review'

          void resume(
            'approve',
            undefined,
            confirmingBrief ? 'Gave the go-ahead.' : 'Approved.',
            confirmingBrief ? review.allowAdjustments() : undefined
          )
        },

        // Likewise on the record — a rejection that leaves no trace looks like the proposal
        // simply went away. The specialist gate can be declined; see `canReject`.
        //
        // `reason` is the specialist gate's optional word on why. It rides the wire as `message`,
        // which the harness reads into the reject decision, and is echoed so the maker's own
        // words stay in the conversation rather than only in the agent's context.
        reject: (reason?: string): void => {
          if (store.canReject()) {
            const said = reason?.trim()

            void resume('reject', said || undefined, said ? `Declined. ${said}` : 'Declined.')
          }
        },

        decideRetarget: (move: boolean): void => {
          if (store.retargetProposal()) {
            void resume(move ? 'approve' : 'reject', undefined, [
              { subject: 'Decision', text: move ? RETARGET_MOVE_ANSWER : RETARGET_KEEP_ANSWER },
            ])
          }
        },

        /**
         * Allow a `read_grant`: this conversation reads the project the maker's page is in,
         * read-only, until it goes idle. The harness re-checks the maker's own access before it
         * grants anything, so this never widens what they can read. `coverage` is what the prompt
         * named, for the line the conversation keeps of the answer.
         */
        allowReadGrant: (coverage: string): void => {
          if (store.interruptType() !== 'read_grant') {
            return
          }

          void resume('approve', undefined, `Allowed this conversation to read ${coverage}.`)
        },

        /** Decline a `read_grant`: the agent answers from the write target alone. */
        declineReadGrant: (coverage: string): void => {
          if (store.interruptType() !== 'read_grant') {
            return
          }

          void resume('reject', undefined, `Did not allow reading ${coverage}.`)
        },

        /**
         * Send the accumulated steering as a `respond`.
         *
         * Never as an approve: the harness will not infer approval from steering, and neither
         * should the UI — a maker adjusting a decision is asking for a revision, not signing off.
         * What is staged and how it reads back belong to the review store; this only carries it.
         */
        sendSteering: (): void => void resume('respond'),

        /** Take or change the maker's feedback on one agent message. */
        setMessageFeedback: (
          messageId: string,
          verdict: HarnessFeedbackVerdict,
          reasons: readonly string[],
          comment: string | null,
          clientContext: FeedbackClientContext
        ): void =>
          changeFeedback(messageId, { messageId, verdict, reasons, comment }, (sessionId, call) =>
            client.putMessageFeedback(
              sessionId,
              messageId,
              { verdict, reasons, comment, clientContext },
              call
            )
          ),

        /** Clear it again. */
        clearMessageFeedback: (messageId: string): void =>
          changeFeedback(messageId, null, (sessionId, call) =>
            client.deleteMessageFeedback(sessionId, messageId, call)
          ),

        /** The maker's Cancel: closes the turn as stopped only once the harness confirms it. */
        cancelTurn: async (): Promise<void> => {
          abandonPendingTurn()

          const sessionId = store.sessionId()

          dropTurnStream()
          lifetime.invalidateRequestChecks()

          if (!sessionId) {
            patchState(store, applyCancel)

            return
          }

          const isLatestRequest = lifetime.latestRequestCheck()
          const appVersionId = store.appVersionId()

          try {
            for (let attempt = 0; ; attempt += 1) {
              let outcome: HarnessCancelOutcome | null = null

              try {
                outcome = await client.cancelTurn(sessionId, turnScope())
              } catch {
                outcome = null
              }

              if (!isLatestRequest()) {
                return
              }

              if (outcome === 'stopped') {
                patchState(store, applyCancel)

                return
              }

              const snapshot = await read(sessionId)

              if (!isLatestRequest() || store.sessionId() !== sessionId) {
                return
              }

              if (snapshot.record?.status === 'running') {
                tail(appVersionId, sessionId, snapshot.record)
                patchState(store, (state) =>
                  withNotice(state, 'info', 'The turn could not be stopped and is still running.', {
                    code: CANCEL_NOT_STOPPED_NOTICE,
                  })
                )

                return
              }

              if (!turnMayBeStarting(snapshot.record) || attempt >= CANCEL_START_RETRIES) {
                show(appVersionId, sessionId, snapshot)

                return
              }

              await new Promise((resolve) => setTimeout(resolve, CANCEL_START_RETRY_MS))

              if (!isLatestRequest()) {
                return
              }
            }
          } catch (error) {
            if (isLatestRequest()) {
              fail(error)
            }
          }
        },

        /** Nothing is closed — the session stays in the harness's list — and the new one opens
         * lazily, on the first turn. False when something is live and nothing was cleared. */
        newSession: (): boolean => {
          if (live()) {
            return false
          }

          const appVersionId = store.appVersionId()

          clear(appVersionId)
          forgetConversation()

          return true
        },

        /** Open another of the maker's conversations, from anywhere — read as its owner. */
        selectSession: async (sessionId: string): Promise<void> => {
          const appVersionId = store.appVersionId()

          if (sessionId === store.sessionId() || sessionId === store.openingSessionId() || live()) {
            return
          }

          clear(appVersionId)

          const isLatestRequest = lifetime.latestRequestCheck()

          patchState(store, { openingSessionId: sessionId })

          try {
            const snapshot = await read(sessionId)

            if (isLatestRequest()) {
              show(appVersionId, sessionId, snapshot)
            }
          } catch (error) {
            if (isLatestRequest()) {
              fail(error)
            }
          } finally {
            finishOpening(sessionId)
          }
        },

        /** Open the branch the conversation writes to, at its head — in whatever project it is. */
        openWriteTarget: async (): Promise<void> => {
          const target = store.writeTarget()

          if (!target || conversationDetails.openingWriteTarget()) {
            return
          }

          conversationDetails.beginOpeningWriteTarget()

          try {
            const opened = await navigator.openWriteTarget(target)

            if (!opened) {
              patchState(store, {
                executionError:
                  'Navigation was cancelled. Resolve unsaved changes, then open the branch again.',
              })
            }
          } catch (error) {
            patchState(store, { executionError: harnessErrorMessage(error) })
          } finally {
            conversationDetails.finishOpeningWriteTarget()
          }
        },

        /** Stop this conversation reading another project. The harness drops what the grant read
         * from every later model call. The grant leaves the rail once the harness has taken the
         * revoke, so a failed read after it cannot leave it listed and revocable twice; that read
         * only reconciles. */
        revokeReadGrant: async (grantId: string): Promise<void> => {
          const sessionId = store.sessionId()

          if (!sessionId || conversationDetails.revokingReadGrantId()) {
            return
          }

          const isLatestRequest = lifetime.latestRequestCheck()

          // Until the revoke and the owner read after it are done, so a second press cannot send
          // the same revoke again.
          conversationDetails.beginRevokingReadGrant(grantId)

          try {
            await client.revokeGrant(sessionId, grantId)

            if (store.sessionId() === sessionId) {
              const revokedAt = new Date().toISOString()

              conversationDetails.markReadGrantRevoked(grantId, revokedAt)
            }

            if (isLatestRequest()) {
              await rereadConversation()
            }
          } catch (error) {
            if (isLatestRequest()) {
              patchState(store, (state) => withNotice(state, 'error', harnessErrorMessage(error)))
            }
          } finally {
            conversationDetails.finishRevokingReadGrant()
          }
        },
      }
    }
  ),
  withHooks({
    onInit(
      store,
      config = inject(HarnessConfigService),
      composerFiles = inject(HarnessComposerFilesStore)
    ) {
      // The conversation follows the maker; the route is where the next turn is made from.
      effect(() => {
        const appVersionId = config.appVersionId() ?? null

        // AI permissions can arrive after the route. Nothing is read until they allow it, on a
        // project page or off one.
        if (!config.available()) {
          return
        }

        untracked(() => store.arrive(appVersionId))
      })

      // The composer's files went up in the workspace the turn acted in, and a turn made from
      // another one cannot read them: moving there takes them off the composer.
      effect(() => {
        const workspaceId = store.uploadWorkspaceId()

        untracked(() => {
          if (
            workspaceId &&
            composerFiles.workspaceId() &&
            workspaceId !== composerFiles.workspaceId()
          ) {
            composerFiles.clear()
          }
        })
      })

      // Kept with the canonical preview and refusal: this runs even before execution is first used.
      // A plan that takes the last one's place — the next one in the conversation after an abandon
      // or a finished run, a revision on the maker's word — is reviewed afresh: the last one's
      // preview and refusal are about a plan that is no longer on screen (conversation-owned).
      let planRevision = store.planRevision()

      effect(() => {
        const revision = store.planRevision()

        untracked(() => {
          if (revision !== planRevision && !store.executionBusy()) {
            patchState(store, { preview: null, executionError: null })
          }
          planRevision = revision
        })
      })
    },
  })
)

/** How often a session is re-read while its turn runs and the relay cannot tail it. */
const TURN_POLL_MS = 3000

/** A Cancel right after Send can reach the harness while it is still preparing the turn. The turn
 * has no events yet, so the cancel is sent again until the harness holds the turn. */
const CANCEL_START_RETRIES = 4
const CANCEL_START_RETRY_MS = 1000

function turnMayBeStarting(record: HarnessSessionRecord | null | undefined): boolean {
  return !!record && record.status === 'idle' && record.events.length === 0
}

/** How many times a cut stream's read-back is tried, and how long apart, before giving up. */
const READ_BACK_ATTEMPTS = 5
const READ_BACK_DELAY_MS = 2000

/** The browser's next `online` event. */
function nextOnline(): Promise<void> {
  return new Promise((resolve) =>
    globalThis.addEventListener('online', () => resolve(), { once: true })
  )
}

/** The conversation is the maker's and goes where they go. `sessionStorage` keeps each tab's
 * choice separate and avoids adopting a days-old id from an in-memory harness after a restart. */
const CONVERSATION_KEY = 'flx-harness-conversation'

function rememberConversation(sessionId: string): void {
  try {
    sessionStorage.setItem(CONVERSATION_KEY, sessionId)
  } catch {
    // A browser refusing storage costs recovery after a reload, nothing more.
  }
}

function recallConversation(): string | null {
  try {
    return sessionStorage.getItem(CONVERSATION_KEY)
  } catch {
    return null
  }
}

function forgetConversation(): void {
  try {
    sessionStorage.removeItem(CONVERSATION_KEY)
  } catch {
    // As above.
  }
}
